import { useState } from "react";
import { api } from "../api";
import { Btn, Card, Field, Select } from "../components/Shared";

/**
 * Every quotation, in every state, as one CSV.
 *
 * The question the facility asked and could not answer from any screen: *what
 * did we offer and what came of it*. The statistics workbook is the nearest
 * thing and it counts accepted quotations only — correctly, for a report about
 * money that was agreed — so a quotation that was sent and turned down was
 * invisible in the one export that looked as though it would know about it.
 * `GET /api/quotations/export.csv` filters no status at all.
 *
 * **What it says before the click is part of the feature.** A row here is a
 * price somebody was quoted, a discount somebody was given and a total somebody
 * agreed to, for a whole facility at once — the way *Documents across groups*
 * beside it says in prose that its export carries names, addresses and IP
 * addresses. Saying it afterwards in the audit log is saying it to the wrong
 * person.
 *
 * The scope is the server's decision, not this panel's: `_stats_scope` answers
 * an admin with the facility and a group admin with a group they manage,
 * whatever `group_id` says. So the picker offers only what the caller may
 * actually be given, and a group admin of one group is *told* which group the
 * file will hold rather than being offered a choice that is not one — a file
 * named "quotations" that silently holds one group's rows is exactly the kind
 * somebody totals and believes.
 */
export default function QuotationsExportPanel({ groups = [], isAdmin = false }) {
  const choices = Array.isArray(groups) ? groups : [];
  // An admin's default is the facility, which is the *absence* of `group_id`
  // rather than a value meaning "all": the route reads a missing one that way,
  // and inventing a sentinel here would be a second scoping rule.
  const [groupId, setGroupId] = useState(isAdmin ? "" : String(choices[0]?.id ?? ""));
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // A group admin picks only when there is something to pick between.
  const mayChoose = isAdmin ? choices.length > 0 : choices.length > 1;
  const only = !isAdmin && choices.length === 1 ? choices[0] : null;

  const download = async () => {
    setError(null);
    setBusy(true);
    try {
      await api.saveFile(
        `/api/quotations/export.csv${groupId ? `?group_id=${groupId}` : ""}`);
    } catch (err) {
      setError(err.message || "The export could not be downloaded.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700 }}>Quotations export</h3>
      <p style={{ margin: "0 0 10px", color: "#6b7280", fontSize: 13 }}>
        One row per offer, in every state — draft, sent, accepted, rejected, expired and
        withdrawn — with the figures taken from the document the signatories were shown, not
        recomputed from today's prices.
      </p>
      <p style={{ margin: "0 0 14px", color: "#6b7280", fontSize: 13 }}>
        The file carries <strong>commercial figures for a whole facility</strong> — every price
        quoted, every discount given and every total a customer agreed to, naming the person it
        was offered to. It is recorded in the log as such.
      </p>

      {only && (
        <p style={{ margin: "0 0 14px", fontSize: 13 }}>
          The file will hold the quotations of <strong>{only.name}</strong>, the group you administer.
        </p>
      )}

      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        {mayChoose && (
          <div data-form-field style={{ minWidth: 220 }}>
            <Field label={isAdmin ? "Group (all of them, unless you pick one)" : "Group"}>
              <Select
                options={choices.map(g => ({ value: String(g.id), label: g.name }))}
                value={groupId}
                onChange={e => setGroupId(e.target.value)}
              />
            </Field>
          </div>
        )}
        <Btn onClick={download} disabled={busy}>
          {busy ? "Preparing…" : "Download CSV"}
        </Btn>
      </div>

      {error && (
        <div role="alert" style={{ marginTop: 10, color: "#b91c1c", fontSize: 13 }}>{error}</div>
      )}
    </Card>
  );
}
